/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */
import ChunkLoadErrorBoundary from 'app/components/ChunkLoadErrorBoundary';
import { defaultConfig } from 'app/config/environment';
import { redirectFromLoginWithToken } from 'app/router/redirectFromLoginWithToken';
import { consumeReturnLocation } from 'app/utils/sessionReturnLocation';
import { ROUTES } from 'app/utils/constants/paths.js';
import { notificationsTypes } from 'app/utils/notifications';
import { isEditorial } from 'app/utils/users';
import { isEdelvives, isInPublisher } from 'app/utils/utils';
import { LayoutContextProvider, changeFavicon, frontoffice } from 'core';
import { getApiHealth } from 'core/crud/appInfo.crud';
import { isLocalhost } from 'core/index.js';
import { MAIN_CONTENT_ID } from 'core/layout/constants';
import { Loading, SkipLink } from 'ui';
import * as auth from 'core/store/auth.duck';
import * as coreEntities from 'core/store/index';
import * as routerHelpers from 'core/utils/RouterHelpers';
import { setTenantColorSnapshot } from 'core/utils/ui';
import { syncPosthogIdentity } from 'core/utils/posthogIdentity';
import { isTeams } from 'core/utils/teamsTab';
import { logToSentry } from 'core/utils/sentry';
import { isStudent, isTeacher } from 'core/utils/user';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { useLastLocation } from 'react-router-last-location';
import { externalRoutes, localhostRoutes, publicRoutes } from './routesConfig.js';
import { usePrivateRoutes } from './usePrivateRoutes';
import { useRouter } from './useRouter';

const NotFound = React.lazy(() => import('app/pages/ContentNotFoundPage/ContentNotFoundPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage/ResetPasswordPage'));

export const APP_IS_PUBLISHER = isInPublisher(defaultConfig) || false;
// http://localhost:3005/auth/login/{token}}/deeplink?link=course&38021190-17e6-11ed-a395-0f156a9b1207&program
let callbackToken = null;

// Lazy loading helpers for third-party libraries
const loadPosthog = async () => {
  const posthog = await import('posthog-js');
  return posthog.default;
};

const loadMicrosoftTeams = async () => {
  const microsoftTeams = await import('@microsoft/teams-js');
  return microsoftTeams;
};

const loadClsx = async () => {
  const clsx = await import('clsx');
  return clsx.default;
};

export const Routes = withRouter(({ history }) => {
  const { isInfantile, loadedApp } = useRouter();
  const lastLocation = useLastLocation();
  const dispatch = useDispatch();
  const privateRoutes = usePrivateRoutes();

  const [teamsContentUrl, setTeamsContentUrl] = useState('');
  const [isTeamsDark, setIsTeamsDark] = useState(false);

  const theme = useSelector((store) => frontoffice.ui.selectors.getTheme(store));
  const userRoleGuid = useSelector((state) => auth.selectors.getUserRoleGuid(state));
  const scopes = useSelector((state) => coreEntities.langs.selectors.getLangScopes(state));
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const tenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  const user = useSelector((state) => auth.selectors.getUser(state));
  const lms = useSelector((state) => coreEntities.organization.selectors.getLms(state));
  checkIfCallbackTokenExists();

  // useEffect(() => {
  //   initAssistant(user, 'TANGERINE');
  // }, []);

  // useEffect(() => {
  //   modifyUser(user);
  // }, [user]);

  useEffect(() => {
    if (user?.guid && tenant?.guid) {
      // initUserForTracking(user, tenant.guid);
      initPosthog().catch((err) => logToSentry('warning', 'PostHog init failed', { error: err instanceof Error ? err : new Error(String(err)) }));
    }
  }, [user?.guid, tenant?.guid, user?.schools?.[0]?.guid]);

  const { isAuthorized, country, isChangePassword } = useSelector(
    ({ auth = {} }) => ({
      isAuthorized: callbackToken ? auth?.user != null && auth?.authToken === callbackToken : auth?.user != null,
      country: auth?.user?.country_guid,
      isChangePassword: Boolean(auth?.changePassword),
    }),
    shallowEqual
  );

  useEffect(() => {
    if (isAuthorized) {
      dispatch(coreEntities.courses.actions.getAllCoursesMessages());
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      checkApiHealth();
      dispatch(frontoffice.notifications.actions.getNotifications({ offset: 0, pageSize: 4, typeNotifications: notificationsTypes.SOCIAL }));
      dispatch(
        frontoffice.notifications.actions.getNotificationsToDo({
          offset: 0,
          pageSize: 4,
          typeNotifications: notificationsTypes.TODO,
        })
      );
      dispatch(frontoffice.notifications.actions.getNotificationsUnread());
      dispatch(frontoffice.notifications.actions.getNotificationsUnreadToDo());
      dispatch(frontoffice.ui.actions.getCustomFonts());
      dispatch(coreEntities.systemSettings.actions.getSystemSettings());
      dispatch(coreEntities.systemSettings.actions.getLrs());
      dispatch(coreEntities.programs.actions.getPrograms({ filters: { isBook: 1, orderBy: 't.name + asc ' } }));
    }
    dispatch(frontoffice.educationLevels.actions.getEducationLevels({ country }));
    dispatch(frontoffice.langs.actions.getLangs({ isFilter: true }));
    dispatch(frontoffice.langs.actions.getLangScopes());
  }, [isAuthorized]);

  useEffect(() => {
    if (!lms) dispatch(coreEntities.organization.actions.getOrganization());
  }, [lms]);

  useEffect(() => {
    if (scopes && scopes.length > 0 && !isEdelvives()) {
      setAppNameAndFavicon();
    }
  }, [scopes]);

  useEffect(() => {
    if (isTeams()) {
      const initMicrosoftTeams = async () => {
        const microsoftTeams = await loadMicrosoftTeams();
        microsoftTeams.app.initialize().then(() => {
          microsoftTeams.app.getContext().then((context) => {
            setIsTeamsDark(context.app.theme === 'dark');
          });
          microsoftTeams.pages.getConfig().then((pageConfig) => {
            if (pageConfig.contentUrl) {
              setTeamsContentUrl(pageConfig.contentUrl.replace(window.location.origin, ''));
            }
          });
        });
      };
      initMicrosoftTeams();
    }
  }, []);

  useEffect(() => {
    const tenantColors = {
      color: getComputedStyle(document.documentElement).getPropertyValue('--color-first'),
      colorAlpha: getComputedStyle(document.documentElement).getPropertyValue('--color-first-alpha'),
      colorGradient1: getComputedStyle(document.documentElement).getPropertyValue('--color-gradient-1'),
      colorGradient2: getComputedStyle(document.documentElement).getPropertyValue('--color-gradient-2'),
    };

    dispatch(frontoffice.ui.actions.setUiColors(tenantColors));

    // Snapshot del color del tenant para los scopes que ignoran la
    // personalización de color del programa (mixin tenant-color-scope). Solo
    // aquí (bootstrap del tema del tenant), nunca desde el path del template.
    setTenantColorSnapshot(tenantColors);

    dispatch(
      frontoffice.ui.actions.setUiExtraProperties({
        borderRadius: getComputedStyle(document.documentElement).getPropertyValue('--border-radius-01'),
      })
    );

    dispatch(
      frontoffice.ui.actions.setUiFonts({
        fontFirst: getComputedStyle(document.documentElement).getPropertyValue('--font-first'),
        fontFirstSemi: getComputedStyle(document.documentElement).getPropertyValue('--font-first-semi'),
        fontSecond: getComputedStyle(document.documentElement).getPropertyValue('--font-second'),
        fontSecondBold: getComputedStyle(document.documentElement).getPropertyValue('--font-second-bold'),
      })
    );
  }, []);

  if (lastLocation && isAuthorized) {
    routerHelpers.saveLastLocation(lastLocation);
  }

  async function checkApiHealth() {
    const response = await getApiHealth();
    if (response?.status !== 200) {
      history.push('/error');
    }
  }

  //TODO: Añadir un else al title y dejar vacío por defecto para que no se vea mientras no llega scopes.
  function setAppNameAndFavicon() {
    let theName = scopes.filter((scope) => scope.scope === 'name');
    if (theName.length > 0) {
      document.title = theName[0].json;
    }
    let theFavicon = scopes.filter((scope) => scope.scope === 'favicon');
    if (theFavicon.length > 0) {
      changeFavicon(theFavicon[0].json);
    }
  }

  function checkIfCallbackTokenExists() {
    const path = window.location.pathname;
    // We check this in case it comes from the backoffice and we are already logged in with another user
    if (path.includes('/auth/login/') || path.includes('/saml/callback/')) {
      const splitted = path.split('/');
      callbackToken = splitted.length > 4 ? splitted[splitted.length - 2] : splitted[splitted.length - 1];
    }
    if (path === '/auth/login') {
      callbackToken = null;
    }
  }

  function getRedirectFromLoginWithToken() {
    return redirectFromLoginWithToken({ dispatch, coreEntities });
  }

  // Wrapped in `render` (not an eager `to`) so getRedirectFromLoginWithToken —
  // which one-shot consumes the saved return location — runs ONLY when the
  // token/SAML path matches, never during a plain /auth/login render (that path
  // is handled by renderLoginRedirect, the intended consumer).
  function renderTokenRedirect() {
    return <Redirect to={getRedirectFromLoginWithToken()} />;
  }

  // Standard email/password login lands on AUTH_LOGIN and does NOT pass through
  // redirectFromLoginWithToken (that only handles token/SAML flows). To honor a
  // one-shot return location saved before a session-timeout re-login, resolve
  // the post-login target here — only when the user is actually on /auth/login.
  // consumeReturnLocation is one-shot (reads + removes), so we cache the resolved
  // target in a ref: if the login Route re-renders before the redirect commits,
  // we reuse the first value instead of re-consuming (which would return null and
  // drop the saved location). Full-page nav on re-login remounts Routes and
  // resets the ref, so each login event resolves exactly once.
  const loginRedirectRef = useRef(null);
  function renderLoginRedirect() {
    if (loginRedirectRef.current === null) {
      loginRedirectRef.current = consumeReturnLocation() ?? ROUTES.HOME;
    }
    return <Redirect to={loginRedirectRef.current} />;
  }

  /**
   * Rutas que se pintan según el estado de sesión. Con returns tempranos en vez de ternarios
   * anidados: la segunda rama comprobaba `isAuthorized && isChangePassword` cuando `isAuthorized`
   * ya era forzosamente `true` por descarte de la primera.
   */
  function renderRoutesBySession() {
    // Redirect to `/auth` when user is not authorized
    if (!isAuthorized) {
      return (
        <Switch>
          {renderRoutes(publicRoutes)}
          <Redirect to={ROUTES.AUTH_LOGIN} />
        </Switch>
      );
    }

    if (isChangePassword) {
      return (
        <Switch>
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Redirect to="/reset-password" />
        </Switch>
      );
    }

    return (
      <Switch>
        <Redirect exact from="/" to={ROUTES.HOME} />
        {isLocalhost() && renderRoutes(localhostRoutes)}
        {isTeams() && <Redirect exact from="/" to={teamsContentUrl} />}
        {isTeams() && <Redirect exact from={ROUTES.HOME} to={teamsContentUrl} />}
        <Redirect from="/course/:courseGuid/unit/:unitGuid/lesson/:lessonGuid" to="/course/:courseGuid/lesson/:lessonGuid" />
        {renderRoutes(privateRoutes)}
        <Route path={ROUTES.AUTH_LOGIN_TOKEN} render={renderTokenRedirect} />
        <Route path={ROUTES.SAML_CALLBACK} render={renderTokenRedirect} />
        <Route path={ROUTES.AUTH_LOGIN} render={renderLoginRedirect} />
        <Redirect from={ROUTES.AUTH_SIGNUP} to={ROUTES.HOME} />
        <Route path="*" component={NotFound} />
      </Switch>
    );
  }

  async function initPosthog() {
    const posthog = await loadPosthog();

    let schoolGuid = null;
    let schoolName = null;
    if (user.schools && user.schools.length > 0) {
      schoolGuid = user.schools[0].guid;
      schoolName = user.schools[0].name;
    }

    syncPosthogIdentity(posthog, user.guid, {
      client: tenant.guid,
      email: user.username,
      name: [user.name, user.lastname].filter(Boolean).join(' '),
      role: isStudent(userRoleGuid) ? 'student' : 'teacher',
      is_tangerine_ai: user.is_tangerine_ai === 1,
      source: isTeams() ? 'MS Teams' : user.source,
      schoolGuid,
      schoolName,
      tenantName: tenant?.name,
      username: user?.username,
    });
  }

  const renderRoutes = (routes) => {
    return routes.map((route, index) => {
      if (route.props) {
        //utilizamos render en lugar de component para pasar props al componente
        return <Route exact key={route.props || index} path={route.path} render={(props) => <route.component {...props} {...route.props} />} />;
      }
      //si no hay props, utilizar component es más eficiente
      return <Route key={index} path={route.path} component={route.component} />;
    });
  };

  const [clsx, setClsx] = useState(null);

  // Load clsx on component mount
  useEffect(() => {
    loadClsx().then(setClsx);
  }, []);

  return (
    /* Create `LayoutContext` from current `history` and `menuConfig`. */
    <>
      {loadedApp && (
        <div
          className={
            clsx
              ? clsx(`theme-${theme}`, `client--${client}`, 'app-content', {
                  'theme-infantile': isInfantile,
                  'app-content--publisher': isEditorial(userRoleGuid),
                  'app-content--student': isStudent(userRoleGuid),
                  'app-content--teacher': isTeacher(userRoleGuid),
                  'app-content--msteams': isTeams(),
                })
              : `theme-${theme} client--${client} app-content`
          }
        >
          <SkipLink targetId={MAIN_CONTENT_ID} />
          <LayoutContextProvider history={history} menuConfig={{}} isTeamsDark={isTeamsDark}>
            {/* {userRoleGuid && isEditorial(userRoleGuid) && <BarMenuContainer />} */}
            <ChunkLoadErrorBoundary>
              <Suspense fallback={<Loading />}>
                <Switch>
                  {renderRoutes(externalRoutes)}
                  {renderRoutesBySession()}
                </Switch>
              </Suspense>
            </ChunkLoadErrorBoundary>
          </LayoutContextProvider>
          {!loadedApp && <Loading />}
        </div>
      )}
    </>
  );
});
